package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.market.payroll.LeaveDay
import com.evoapps.evoshift.domain.market.payroll.LeaveKind
import com.evoapps.evoshift.domain.market.payroll.MonthWorkInput
import com.evoapps.evoshift.domain.market.payroll.ProfileShare
import com.evoapps.evoshift.domain.market.payroll.WorkedSpan
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.requests.RequestCatalog
import com.evoapps.evoshift.domain.requests.RequestCounters
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/**
 * SH-57: the facts of one person's month that the pay estimate needs beyond the month's own
 * shifts, shared by the payroll (financial managers) and the person's own earnings card.
 */
object MonthWork {
    fun firstDay(year: Int, month: Int) = LocalDate(year, month, 1)
    fun lastDay(year: Int, month: Int): LocalDate = firstDay(year, month).plus(DatePeriod(months = 1)).minus(DatePeriod(days = 1))

    /** The roster ids of the 12 months before [year]/[month] at [workplaceId] (sick pay and holiday pay averages). */
    fun earlierRosterIds(workplaceId: String, year: Int, month: Int): List<String> = (1..12).map { back ->
        val index = year * 12 + month - 1 - back
        RosterIds.of(workplaceId, index / 12, index % 12 + 1)
    }

    /**
     * Approved sick leave and annual leave (SH-136: leave on demand too, which is annual leave
     * taken at short notice, art. 167(2) of the Labour Code) of [userId] at [workplaceId] (or at all their workplaces),
     * one entry per day, from 1 January of [year] to the end of [month].
     */
    fun leaveDays(absences: List<Absence>, userId: String, workplaceId: String, year: Int, month: Int): List<LeaveDay> {
        val from = LocalDate(year, 1, 1)
        val to = lastDay(year, month)
        return absences.asSequence()
            .filter { it.userId == userId && it.status == AbsenceStatus.APPROVED && (it.workplaceId == null || it.workplaceId == workplaceId) }
            .flatMap { absence ->
                // SH-223: every type, by what it means for pay (RequestCatalog.leaveKind).
                val kind = RequestCatalog.leaveKind(absence.type, absence.sickPayFull)
                val start = runCatching { LocalDate.parse(absence.startDate.take(10)) }.getOrNull() ?: return@flatMap emptySequence()
                val end = runCatching { LocalDate.parse(absence.endDate.take(10)) }.getOrNull() ?: start
                // Part of one day: its hours.
                val hours = if (absence.isHourly) RequestCounters.hoursOf(absence.startTime, absence.endTime) else null
                generateSequence(maxOf(start, from)) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= minOf(end, to) }
                    .map { LeaveDay(it, kind, hours) }
            }
            .toList()
    }

    /**
     * SH-223: approved overtime and work events of [userId] at [workplaceId] in [year]/[month], as
     * worked time outside the shifts: overtime by its hours; a work event by its hours, or 8 hours
     * from 08:00 on each working day it covers, unless a shift that day is worked already (the
     * event then is that day's work, not more of it).
     */
    fun extraWork(
        requests: List<WorkRequest>, userId: String, workplaceId: String, year: Int, month: Int,
        workedShiftDates: Set<String> = emptySet(), isWorkingDay: (LocalDate) -> Boolean = { true }
    ): List<WorkedSpan> {
        val first = firstDay(year, month)
        val last = lastDay(year, month)
        return requests.asSequence()
            .filter { it.userId == userId && it.workplaceId == workplaceId && it.status == AbsenceStatus.APPROVED }
            .filter { it.kind == WorkRequestKind.OVERTIME || it.kind == WorkRequestKind.WORK_EVENT }
            .flatMap { request ->
                val start = runCatching { LocalDate.parse(request.startDate.take(10)) }.getOrNull() ?: return@flatMap emptySequence()
                val end = runCatching { LocalDate.parse(request.endDate.take(10)) }.getOrNull() ?: start
                generateSequence(maxOf(start, first)) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= minOf(end, last) }.mapNotNull { day ->
                    if (request.kind == WorkRequestKind.WORK_EVENT && day.toString() in workedShiftDates) return@mapNotNull null
                    val from = request.startTime?.let { RequestCounters.time(it) }
                    val to = request.endTime?.let { RequestCounters.time(it) }
                    when {
                        from != null && to != null && to > from -> WorkedSpan(LocalDateTime(day, from), LocalDateTime(day, to))
                        from != null && to != null && to < from -> WorkedSpan(LocalDateTime(day, from), LocalDateTime(day.plus(DatePeriod(days = 1)), to))
                        request.kind == WorkRequestKind.WORK_EVENT && isWorkingDay(day) ->
                            WorkedSpan(LocalDateTime(day, LocalTime(8, 0)), LocalDateTime(day, LocalTime(16, 0)))
                        else -> null
                    }
                }
            }
            .toList()
    }

    /**
     * SH-149: the month's pay split by the payroll facts in force (PayrollRules.payslip with
     * shares): hourly pay by what each day's shifts earned ([paidByDay]: "YYYY-MM-DD" to pay), a
     * salary (or a month without paid shifts) by calendar day. The app estimates by the day worked;
     * the accountant applies tax reliefs by the month the pay is paid in.
     */
    fun profileShares(engagement: FinancialEngagement, year: Int, month: Int, paidByDay: Map<String, Double>): List<ProfileShare> {
        val first = firstDay(year, month)
        val last = lastDay(year, month)
        val salary = engagement.compensationOn(last.toString()) is CompensationModel.MonthlyFixed
        val worked = paidByDay.filter { (day, pay) -> pay > 0 && day.take(10) >= first.toString() && day.take(10) <= last.toString() }
        val weights = if (!salary && worked.isNotEmpty()) worked
        else generateSequence(first) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= last }.associate { it.toString() to 1.0 }
        return weights.map { (day, weight) -> ProfileShare(engagement.profileOn(day), weight) }
    }

    /** A shift on a day of leave that was not clocked was not worked: it is not paid. SH-223: a whole day of it. */
    fun isOnLeave(shift: Shift, leave: Collection<LeaveDay>): Boolean {
        if (leave.none { it.hours == null }) return false
        val clocked = shift.actualTime?.let { it.start.isNotEmpty() && it.end.isNotEmpty() } == true
        if (clocked) return false
        val day = runCatching { LocalDate.parse(shift.date.ifEmpty { shift.plannedTime.start }.take(10)) }.getOrNull() ?: return false
        return leave.any { it.date == day && it.hours == null }
    }

    /** The estimate's input for [engagement]: the month-end terms, the rate history, leave and earlier work. */
    fun input(
        year: Int,
        month: Int,
        engagement: FinancialEngagement,
        shifts: List<WorkedSpan>,
        settings: WorkplacePayrollSettings,
        bonuses: Double,
        leave: List<LeaveDay> = emptyList(),
        earlierWork: List<WorkedSpan> = emptyList(),
        extraWork: List<WorkedSpan> = emptyList()
    ) = MonthWorkInput(
        year = year,
        month = month,
        // SH-212: the month-end contract (its type and declared hours), as the pay terms.
        contract = engagement.contractOn(lastDay(year, month).toString()).contract,
        compensation = engagement.compensationOn(lastDay(year, month).toString()),
        declaredHours = engagement.contractOn(lastDay(year, month).toString()).declaredNumberOfHours,
        shifts = shifts,
        settings = settings,
        otherCountedPay = bonuses,
        // SH-149: the month-end facts, and each day's for the leave pay.
        profile = engagement.profileOn(lastDay(year, month).toString()),
        profilePeriods = engagement.profilePeriods,
        ratePeriods = if (engagement.rateHistory.isEmpty() && engagement.compensationEffectiveFrom == null) emptyList() else engagement.ratePeriods,
        leave = leave,
        earlierWork = earlierWork,
        extraWork = extraWork
    )
}
